import React, { useState } from 'react';
import {
  Box,
  Badge,
  IconButton,
  Popover,
  Typography,
  List,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Drawer,
  Paper,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks';
import { NotificationItem } from './NotificationItem';

interface NotificationBellProps {
  lightMode?: boolean; // For header use on dark background
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ lightMode = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      setMobileOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMobileOpen(false);
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const open = Boolean(anchorEl);

  // Notification content for mobile drawer (full height, scrollable list)
  const mobileNotificationContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none' }}
            >
              Mark all read
            </Button>
          )}
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Notification list - scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isLoading && notifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onClick={handleClose}
                  dense={false}
                />
                {index < notifications.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Load more */}
        {hasMore && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button
              onClick={loadMore}
              disabled={isLoading}
              sx={{ textTransform: 'none' }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Load more'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Footer - sticky */}
      <Box
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          fullWidth
          onClick={handleViewAll}
          sx={{ textTransform: 'none' }}
        >
          View all notifications
        </Button>
      </Box>
    </Box>
  );

  // Notification content for desktop popover (fixed height with scrollable list)
  const desktopNotificationContent = (
    <>
      {/* Header - fixed */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={handleMarkAllAsRead}
            sx={{ textTransform: 'none' }}
          >
            Mark all read
          </Button>
        )}
      </Box>

      {/* Notification list - scrollable, fixed max height */}
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: 320, // Fixed height for scrolling
        }}
      >
        {isLoading && notifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.slice(0, 5).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onClick={handleClose}
                  dense
                />
                {index < Math.min(notifications.length, 5) - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer - fixed at bottom */}
      <Box
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <Button
          fullWidth
          onClick={handleViewAll}
          sx={{ textTransform: 'none' }}
        >
          View all notifications
        </Button>
      </Box>
    </>
  );

  return (
    <>
      <IconButton
        color={lightMode ? 'inherit' : 'default'}
        onClick={handleOpen}
        aria-label={`${unreadCount} notifications`}
        sx={{
          // Subtle animation when there are unread notifications
          animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.05)' },
          },
        }}
      >
        <Badge
          badgeContent={unreadCount > 99 ? '99+' : unreadCount}
          color="error"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              minWidth: 16,
              height: 16,
              padding: '0 4px',
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsIcon />
          ) : (
            <NotificationsNoneIcon />
          )}
        </Badge>
      </IconButton>

      {/* Desktop Popover */}
      {!isMobile && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 360,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {desktopNotificationContent}
        </Popover>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 400,
            },
          }}
        >
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            elevation={0}
          >
            {mobileNotificationContent}
          </Paper>
        </Drawer>
      )}
    </>
  );
};
